import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';

import type { RoadmapItem, RoadmapStatus } from '@smartapply/shared';

import { fetchRoadmap, voteForItem, SiteApiError } from '../lib/api.js';
import { Stamp } from './Stamp.js';

const STATUS_ORDER: ReadonlyArray<RoadmapStatus> = [
  'in_progress',
  'planned',
  'considered',
  'shipped',
];

const STATUS_META: Record<
  RoadmapStatus,
  { label: string; description: string; tone: 'default' | 'positive' | 'suggestion' | 'critical' }
> = {
  in_progress: {
    label: 'em obras',
    description: 'sendo construído agora',
    tone: 'suggestion',
  },
  planned: {
    label: 'planejado',
    description: 'vai sair em breve, na ordem dos votos',
    tone: 'default',
  },
  considered: {
    label: 'em consideração',
    description: 'depende da votação pra entrar na fila',
    tone: 'default',
  },
  shipped: {
    label: 'entregue',
    description: 'já está rodando',
    tone: 'positive',
  },
  rejected: {
    label: 'descartado',
    description: 'avaliado e fora do escopo',
    tone: 'critical',
  },
};

export function RoadmapSection() {
  const [items, setItems] = useState<RoadmapItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const res = await fetchRoadmap();
      setItems(res.items);
    } catch (e) {
      setError(e instanceof SiteApiError ? e.message : 'Falha ao carregar roadmap.');
    }
  }

  async function handleVote(item: RoadmapItem) {
    if (item.hasVoted || voting) return;
    setVoting(item.id);
    setError(null);
    try {
      const res = await voteForItem(item.id);
      setItems((prev) =>
        prev
          ? prev.map((it) =>
              it.id === item.id
                ? { ...it, votesCount: res.votesCount, hasVoted: res.voted || it.hasVoted }
                : it,
            )
          : prev,
      );
    } catch (e) {
      setError(e instanceof SiteApiError ? e.message : 'Falha ao votar.');
    } finally {
      setVoting(null);
    }
  }

  const grouped = useMemo(() => {
    if (!items) return null;
    const map = new Map<RoadmapStatus, RoadmapItem[]>();
    for (const status of STATUS_ORDER) map.set(status, []);
    for (const it of items) {
      const bucket = map.get(it.status as RoadmapStatus);
      if (bucket) bucket.push(it);
    }
    return map;
  }, [items]);

  return (
    <section
      id="roadmap"
      className="border-b border-[var(--border-subtle)] bg-[var(--bg-paper-elevated)] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <header className="max-w-2xl">
          <Stamp variant="suggestion">construído com você</Stamp>
          <h2 className="mt-4 font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px]">
            Roadmap aberto<span className="text-[var(--accent-saffron)]">.</span>
          </h2>
          <p className="mt-3 font-sans text-base text-[var(--ink-secondary)]">
            Vote no que você quer ver primeiro. Voto anônimo (1 por browser). Os itens em{' '}
            <em>consideração</em> só viram <em>planejados</em> depois que a comunidade votar.
            Sugestões viram itens novos depois de revisão técnica.
          </p>
        </header>

        {error && (
          <p className="mt-6 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
        )}

        {items === null && !error && (
          <p className="mt-12 font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            carregando roadmap...
          </p>
        )}

        {grouped && (
          <div className="mt-14 space-y-14">
            {STATUS_ORDER.map((status) => {
              const list = grouped.get(status) ?? [];
              if (list.length === 0) return null;
              const meta = STATUS_META[status];
              return (
                <div key={status}>
                  <div className="flex items-baseline gap-3 border-b border-[var(--border-default)] pb-3">
                    <Stamp variant={meta.tone}>{meta.label}</Stamp>
                    <span className="font-sans text-xs italic text-[var(--ink-tertiary)]">
                      {meta.description}
                    </span>
                    <span className="ml-auto font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
                      {list.length} {list.length === 1 ? 'item' : 'itens'}
                    </span>
                  </div>

                  <ul className="mt-5 grid gap-4 md:grid-cols-2">
                    {list.map((item) => (
                      <RoadmapCard
                        key={item.id}
                        item={item}
                        onVote={handleVote}
                        voting={voting === item.id}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function RoadmapCard({
  item,
  onVote,
  voting,
}: {
  item: RoadmapItem;
  onVote: (item: RoadmapItem) => void;
  voting: boolean;
}) {
  const canVote = item.status !== 'shipped' && item.status !== 'rejected';
  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="card-paper rounded-sm px-5 py-4"
      style={{ borderLeft: `3px solid ${accentByStatus(item.status as RoadmapStatus)}` }}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          {item.category}
        </p>
        {item.shippedAt && (
          <p className="font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
            · entregue em {new Date(item.shippedAt).toLocaleDateString('pt-BR')}
          </p>
        )}
      </div>
      <h3 className="mt-1 font-serif text-base font-medium text-[var(--ink-primary)]">
        {item.title}
      </h3>
      <p className="mt-1 font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">
        {item.description}
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] pt-3">
        {canVote ? (
          <button
            type="button"
            onClick={() => onVote(item)}
            disabled={item.hasVoted || voting}
            aria-label={
              item.hasVoted
                ? `Você já votou em "${item.title}"`
                : `Votar em "${item.title}"`
            }
            className="group inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 font-sans text-sm transition-all hover:shadow-sm disabled:cursor-not-allowed"
            style={{
              borderColor: item.hasVoted
                ? 'var(--accent-forest-border)'
                : 'var(--ink-primary)',
              backgroundColor: item.hasVoted
                ? 'var(--accent-forest-bg)'
                : 'var(--ink-primary)',
              color: item.hasVoted
                ? 'var(--accent-forest)'
                : 'var(--bg-paper)',
              cursor: item.hasVoted ? 'default' : 'pointer',
            }}
          >
            <span aria-hidden className="font-mono text-base leading-none">
              {item.hasVoted ? '✓' : '↑'}
            </span>
            <span className="font-medium">
              {item.hasVoted ? 'Você votou' : voting ? 'Votando...' : 'Votar'}
            </span>
          </button>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">
            {item.status === 'shipped' ? 'já entregue' : 'fora de escopo'}
          </span>
        )}

        <p className="font-mono text-sm tabular-nums text-[var(--ink-secondary)]">
          <span className="font-medium">{item.votesCount}</span>{' '}
          <span className="text-[var(--ink-tertiary)]">
            {item.votesCount === 1 ? 'voto' : 'votos'}
          </span>
        </p>
      </div>
    </motion.li>
  );
}

function accentByStatus(status: RoadmapStatus): string {
  switch (status) {
    case 'in_progress':
      return 'var(--accent-saffron)';
    case 'planned':
      return 'var(--ink-secondary)';
    case 'considered':
      return 'var(--ink-tertiary)';
    case 'shipped':
      return 'var(--accent-forest)';
    case 'rejected':
      return 'var(--accent-crimson)';
    default:
      return 'var(--border-default)';
  }
}
